import { AnyAction } from 'redux';
import {
  PRODUCT_LISTING_RECEIVE,
  PRODUCT_LISTING_SET_PROP,
  PRODUCT_LISTING_SET_IS_LOADING,
  PRODUCT_LISTING_SET_PRODUCTS,
} from '../constants';
import { RootState } from '../reducers';
import { get, post } from '../Services/http';
import { getProductFilteringAsQueryString } from './ProductFiltering.action';

export const fetchProducts = (startIndex: number, endIndex: number) => async (
  dispatch,
  getState: () => RootState
): Promise<any> => {
  dispatch(setIsLoadingMore(true));
  const productListingState = getState().productListing;
  const { productsPerRow, totalProductsCount } = productListingState;
  const productFiltering = getState().productFiltering;
  try {
    // Calculate number of products to skip and take
    const skip = startIndex * productsPerRow;
    let take = (endIndex - startIndex) * productsPerRow;

    // Make sure we don't take more then what's left until we reach totalProductsCount
    if (skip + take > totalProductsCount) {
      take = totalProductsCount - skip;
    }

    const filterParams = getProductFilteringAsQueryString(productFiltering);
    const response = await get(
      `/api/products?skip=${skip}&take=${take}&${filterParams}`
    );
    const newProductListing = await response.json();

    // Replace placeholder cards with the actual products
    const _products = [...getState().productListing.products];
    _products.splice(skip, take, ...newProductListing.products);

    dispatch(setProducts(_products, newProductListing.gtmViewModel));
  } catch (err) {
    // TODO Error handling, retry once or twice? Not in MOX-86
    // If retry, we need to move setIsLoadingMore(false) to both "try" and "catch", instead of "finally"
    console.log(err);
    throw err;
  } finally {
    dispatch(setIsLoadingMore(false));
  }
};

export const addPlaceholderProducts = (
  startIndex: number,
  endIndex: number
) => (dispatch, getState: () => RootState) => {
  const {
    products,
    productsPerRow,
    totalProductsCount,
  } = getState().productListing;

  // Calculate number of placeholders to add, based on the start and end index
  const skip = startIndex * productsPerRow;
  let placeholderCount = (endIndex - startIndex) * productsPerRow;

  // Make sure we don't add more placeholders than totalProductsCount
  if (skip + placeholderCount > totalProductsCount) {
    placeholderCount = totalProductsCount - skip;
  }

  const placeholderProducts = Array.from({ length: placeholderCount });
  const _products = [...products];
  _products.splice(
    startIndex * productsPerRow,
    placeholderCount,
    ...placeholderProducts
  );
  dispatch(setProducts(_products));
};

export const receiveProductListing = (productListing) => ({
  type: PRODUCT_LISTING_RECEIVE,
  payload: productListing,
});

const setProductListingProp = (key, value) => ({
  type: PRODUCT_LISTING_SET_PROP,
  payload: {
    [key]: value,
  },
});

export const setLoadMoreFrom = (loadMoreFrom) => (dispatch) =>
  dispatch(setProductListingProp('loadMoreFrom', loadMoreFrom));

export const setLoadReverseFrom = (loadReverseFrom) => (dispatch) =>
  dispatch(setProductListingProp('loadReverseFrom', loadReverseFrom));

export const setIsLoadingReverse = (isLoadingReverse) => (dispatch) =>
  dispatch(setProductListingProp('isLoadingReverse', isLoadingReverse));

export const setIsLoadingMore = (isLoadingMore) => (dispatch) =>
  dispatch(setProductListingProp('isLoadingMore', isLoadingMore));

export const setScrollIndex = (scrollIndex) => (dispatch) =>
  dispatch(setProductListingProp('scrollIndex', scrollIndex));

export const setTotalProductsCount = (totalProductsCount) => (dispatch) =>
  dispatch(setProductListingProp('totalProductsCount', totalProductsCount));

export const setProductsPerRow = (productsPerRow) => (dispatch) =>
  dispatch(setProductListingProp('productsPerRow', productsPerRow));

export const setProducts = (products, gtmViewModel = null) => ({
  type: PRODUCT_LISTING_SET_PRODUCTS,
  payload: {
    products,
    gtmViewModel,
  },
});

export const setProductListingType = (productListingType) => async (
  dispatch
) => {
  const res = await post('/api/products/listingtype', { productListingType });
  if (res.ok) {
    dispatch(setProductListingProp('productListingType', productListingType));
  }
};

export const setIsLoadingProductListing = (isLoading) => ({
  type: PRODUCT_LISTING_SET_IS_LOADING,
  payload: {
    isLoading,
  },
});
